import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';

export interface FixedScheduleValue {
  weeklyHours: string;
}

export interface FixedScheduleProps
  extends Omit<Partial<FieldRenderProps>, 'onChange' | 'value'> {
  onChange: (e: { value: FixedScheduleValue }) => void;
  value?: FixedScheduleValue;
  processedProps?: boolean;
}

const MAX_WEEKLY_HOURS = 168; // 7 × 24
const MAX_ERROR_TEXT = `Max value: ${MAX_WEEKLY_HOURS} hrs.`;

const INPUT_BASE =
  'tw-w-[70px] tw-h-8 tw-text-center tw-text-base ' +
  'tw-bg-unm-bg-light tw-border tw-rounded-lg tw-outline-none';
const INPUT_ENABLED =
  'tw-border-unm-border1 focus:tw-border-unm-interactive01-400 tw-cursor-text';
const INPUT_ERROR =
  'tw-border-2 tw-border-[#B42318] focus:tw-border-[#B42318] tw-cursor-text';

const inputClass = (error: boolean) =>
  `${INPUT_BASE} ${INPUT_ENABLED} ${error ? INPUT_ERROR : ''}`;

const isOver = (v: string) =>
  v !== '' &&
  !Number.isNaN(Number.parseInt(v)) &&
  Number.parseInt(v) > MAX_WEEKLY_HOURS;

export function FixedSchedule({
  onChange,
  value,
  onBlur,
  processedProps,
}: Readonly<FixedScheduleProps>) {
  const [weeklyHours, setWeeklyHours] = React.useState(
    value?.weeklyHours ?? '',
  );
  const [validationError, setValidationError] = React.useState('');

  React.useEffect(() => {
    if (!processedProps) return;
    const hrs = value?.weeklyHours ?? '';
    setWeeklyHours(hrs);
    setValidationError('');
    onChange({ value: { weeklyHours: hrs } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedProps]);

  const onHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (
      v !== '' &&
      !Number.isNaN(Number.parseInt(v)) &&
      Number.parseInt(v) > MAX_WEEKLY_HOURS
    ) {
      setValidationError(MAX_ERROR_TEXT);
    } else {
      setValidationError('');
    }
    setWeeklyHours(v);
    onChange({ value: { weeklyHours: v } });
  };

  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  return (
    <fieldset
      id="form-variable-fixed-schedule"
      className="tw-w-full tw-border-0 tw-p-0 tw-m-0"
      onBlur={handleBlur}
    >
      <div className="tw-bg-white tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.20)] tw-rounded-[8px] tw-px-5 tw-py-8 tw-flex tw-flex-col tw-gap-6">
        <p className="tw-text-base tw-text-unm-adhoc2">
          Since your working days vary, we just need to know your total weekly
          hours.
        </p>
        <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2">
          How many hours do you work each week?
        </p>
        <div className="tw-flex tw-items-center tw-gap-3">
          <input
            type="number"
            id="form-variable-fixed-schedule-hours"
            min={0}
            max={MAX_WEEKLY_HOURS}
            value={weeklyHours}
            placeholder="e.g. 40"
            onChange={onHoursChange}
            className={inputClass(isOver(weeklyHours))}
            aria-label="Weekly hours"
          />
          <span className="tw-text-base tw-text-unm-adhoc2">
            hours per week
          </span>
        </div>
        {validationError && (
          <p
            id="form-variable-fixed-schedule-error"
            className="tw-text-sm tw-font-semibold tw-text-[#B42318]"
          >
            {validationError}
          </p>
        )}
      </div>
    </fieldset>
  );
}
